/** @format */

import React, { useState, useEffect } from "react";
import {
  XMarkIcon,
  ShieldCheckIcon,
  AcademicCapIcon,
  UserIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-hot-toast";

const RoleAssignmentModal = ({
  isOpen,
  onClose,
  user = null,
  onRoleUpdate,
}) => {
  const [selectedRole, setSelectedRole] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user) {
      // Handle role as array or string
      const userRole = Array.isArray(user.role) ? user.role[0] : user.role;
      setSelectedRole(userRole || "student");
    } else {
      setSelectedRole("student");
    }
    setErrors({});
  }, [user]);

  const validateForm = () => {
    const newErrors = {};

    if (!selectedRole) {
      newErrors.role = "Please select a role";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const roleData = {
        role: [selectedRole], // Convert to array format for consistency
        updatedAt: new Date(),
      };

      await onRoleUpdate(roleData, user?.id);
    } catch (error) {
      console.error("Error updating user role:", error);
      toast.error("Failed to update user role. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getRoleDescription = (role) => {
    switch (role) {
      case "admin":
        return "Full system access, can manage all users, content, and settings";
      case "author":
        return "Can create and manage courses, books, and content";
      case "student":
        return "Can access assigned content and participate in courses";
      default:
        return "";
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "admin":
        return <ShieldCheckIcon className="h-5 w-5 text-red-600" />;
      case "author":
        return <AcademicCapIcon className="h-5 w-5 text-blue-600" />;
      case "student":
        return <UserIcon className="h-5 w-5 text-green-600" />;
      default:
        return <UserIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "admin":
        return "border-red-200 bg-red-50 text-red-700";
      case "author":
        return "border-blue-200 bg-blue-50 text-blue-700";
      case "student":
        return "border-green-200 bg-green-50 text-green-700";
      default:
        return "border-gray-200 bg-gray-50 text-gray-700";
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        <div className="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <ShieldCheckIcon className="h-6 w-6 text-blue-600 mr-3" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    Assign User Role
                  </h3>
                  <p className="text-sm text-gray-500">
                    {user
                      ? `Update role for ${user.name}`
                      : "Select a role for this user"}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Current User Info */}
            {user && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    {user.photoURL ? (
                      <img
                        className="h-10 w-10 rounded-full"
                        src={user.photoURL}
                        alt={user.name}
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                        <UserIcon className="h-6 w-6 text-gray-600" />
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {user.name}
                    </p>
                    <p className="text-sm text-gray-500">{user.email}</p>
                    <p className="text-xs text-gray-400">
                      Current role:{" "}
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(
                          Array.isArray(user.role) ? user.role[0] : user.role
                        )}`}
                      >
                        {getRoleIcon(
                          Array.isArray(user.role) ? user.role[0] : user.role
                        )}
                        <span className="ml-1">
                          {Array.isArray(user.role)
                            ? user.role[0]
                            : user.role || "student"}
                        </span>
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Role Selection */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Select User Role *
                </label>
                <div className="space-y-3">
                  {["admin", "author", "student"].map((role) => (
                    <label
                      key={role}
                      className={`relative flex cursor-pointer rounded-lg border p-4 hover:bg-gray-50 focus:outline-none ${
                        selectedRole === role
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-300"
                      }`}
                    >
                      <input
                        type="radio"
                        name="role"
                        value={role}
                        checked={selectedRole === role}
                        onChange={(e) => setSelectedRole(e.target.value)}
                        className="sr-only"
                      />
                      <div className="flex w-full items-center justify-between">
                        <div className="flex items-center">
                          <div className="text-sm">
                            <div className="flex items-center">
                              {getRoleIcon(role)}
                              <span className="ml-2 font-medium text-gray-900 capitalize">
                                {role}
                              </span>
                            </div>
                            <p className="text-gray-500 text-xs mt-1">
                              {getRoleDescription(role)}
                            </p>
                          </div>
                        </div>
                        {selectedRole === role && (
                          <CheckCircleIcon className="h-5 w-5 text-blue-600" />
                        )}
                      </div>
                    </label>
                  ))}
                </div>
                {errors.role && (
                  <p className="mt-2 text-sm text-red-600">{errors.role}</p>
                )}
              </div>

              {/* Role Change Warning */}
              {user &&
                selectedRole !==
                  (Array.isArray(user.role) ? user.role[0] : user.role) && (
                  <div className="rounded-md bg-yellow-50 p-4">
                    <div className="flex">
                      <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">
                          Role Change Warning
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <p>
                            Changing a user's role from{" "}
                            <strong>
                              {(Array.isArray(user.role)
                                ? user.role[0]
                                : user.role) || "student"}
                            </strong>{" "}
                            to <strong>{selectedRole}</strong> will affect their
                            access permissions.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
            </form>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={loading}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Updating...
                </>
              ) : (
                "Update Role"
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoleAssignmentModal;
